import type { QueryKey } from "@tanstack/react-query";

import type { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";

const RANK_TRACKER_QUERY_KEY_PREFIX = "rt" as const;
type RankTrackerQueryPrefixType = typeof RANK_TRACKER_QUERY_KEY_PREFIX;
type CampaignIdType = string;
type GenericRankTrackerQueryKey<TQueryKey extends QueryKey = QueryKey> = [
    RankTrackerQueryPrefixType,
    RankTrackerQueryType,
    ...TQueryKey,
];
type CampaignQueryKey<TQueryKey extends QueryKey = QueryKey> = [
    RankTrackerQueryPrefixType,
    CampaignIdType,
    RankTrackerQueryType,
    ...TQueryKey,
];

/**
 * Extracts the campaign ID from a campaign object.
 *
 * @param campaignId - The campaign id
 * @returns The campaign ID string
 */
export const getCampaignQueryKeyPrefix = (campaignId: CampaignIdType): [RankTrackerQueryPrefixType, CampaignIdType] => {
    return [RANK_TRACKER_QUERY_KEY_PREFIX, campaignId];
};

/**
 * Creates a consolidated cache key for Rank Tracker queries involving a
 * specific campaign OR all campaigns. Ensures the campaign ID and query type
 * are included in the key while allowing selective invalidation based on what
 * data types the query depends on.
 *
 * @param campaignId - The campaign id.
 * @param queryType - The type of query (required for selective invalidation).
 * @param extraKey - Any additional keys to include in the cache key.
 * @returns A readonly array suitable for use as a TanStack Query key.
 */
export const createCampaignQueryKey = <TQueryKey extends QueryKey = QueryKey>(
    campaignId: CampaignIdType | undefined = "invalid",
    queryType: RankTrackerQueryType,
    extraKey: TQueryKey = [] as unknown as TQueryKey,
): CampaignQueryKey<TQueryKey> => {
    const prefix = getCampaignQueryKeyPrefix(campaignId);
    return [...prefix, queryType, ...extraKey];
};

/**
 * Creates a query key for Rank Tracker queries that are not tied to a specific campaign.
 * Ensures the query type is included in the key while allowing selective invalidation
 * based on what data types the query depends on.
 *
 * @param queryType - The type of query (required for selective invalidation).
 * @param extraKey - Any additional keys to include in the cache key.
 * @returns A readonly array suitable for use as a TanStack Query key.
 */
export const createRankTrackerQueryKey = <TQueryKey extends QueryKey = QueryKey>(
    queryType: RankTrackerQueryType,
    extraKey: TQueryKey = [] as unknown as TQueryKey,
): GenericRankTrackerQueryKey => {
    return [RANK_TRACKER_QUERY_KEY_PREFIX, queryType, ...extraKey];
};
